export function messagesFromGlob(modules) {
  const messages = {};
  Object.entries(modules).forEach(([filePath, mod]) => {
    const matched = filePath.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      messages[matched[1]] = mod.default ?? mod;
    }
  });
  return messages;
}

export function mergeLocaleMessages(target, ...sources) {
  sources.forEach((toImport) => {
    Object.keys(toImport).forEach((locale) => {
      if (target[locale] === undefined) {
        target[locale] = {};
      }
      Object.keys(toImport[locale]).forEach((key) => {
        if (target[locale][key] === undefined) {
          target[locale][key] = toImport[locale][key];
        }
      });
    });
  });
  return target;
}

/**
 * Extract locale code from a Vite glob path (e.g. ".../fr-BE.json" → "fr-BE").
 */
export function localeCodeFromPath(filePath) {
  const matched =
    String(filePath).match(/\/([A-Za-z0-9-_]+)\.json(?:\?.*)?$/i) ||
    String(filePath).match(/([A-Za-z0-9-_]+)\./i);
  return matched?.[1] ?? null;
}

/**
 * Normalize locale to vue-i18n form: en_gb / en-gb → en-GB.
 */
export function normalizeI18nLocale(locale) {
  if (!locale || typeof locale !== 'string') {
    return 'en-GB';
  }
  const parts = locale.replace('_', '-').split('-');
  if (parts.length < 2) {
    return parts[0];
  }
  return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
}

/**
 * Load locale JSON modules (from import.meta.glob without eager) for the given locales.
 * @param {Record<string, () => Promise<any>>} modules
 * @param {string|string[]} locales
 */
export async function loadLocaleMessagesFromGlob(modules, locales) {
  const wanted = new Set(
    (Array.isArray(locales) ? locales : [locales])
      .filter(Boolean)
      .map((l) => normalizeI18nLocale(l)),
  );
  const messages = {};
  const entries = Object.entries(modules).filter(([path]) => {
    const code = localeCodeFromPath(path);
    return code && wanted.has(code);
  });

  await Promise.all(
    entries.map(async ([path, loader]) => {
      const code = localeCodeFromPath(path);
      const mod = typeof loader === 'function' ? await loader() : loader;
      const data = mod?.default ?? mod;
      if (messages[code] === undefined) {
        messages[code] = {};
      }
      Object.keys(data).forEach((key) => {
        if (messages[code][key] === undefined) {
          messages[code][key] = data[key];
        }
      });
    }),
  );

  return messages;
}

/**
 * Set the active locale on a vue-i18n instance (legacy or composition mode).
 */
export function applyI18nLocale(i18nInstance, locale) {
  if (!i18nInstance || !locale) {
    return;
  }
  const normalized = normalizeI18nLocale(locale);
  if (i18nInstance.global?.locale != null) {
    if (typeof i18nInstance.global.locale === 'object') {
      i18nInstance.global.locale.value = normalized;
    } else {
      i18nInstance.global.locale = normalized;
    }
  }
  // Some call sites still assign on the root instance
  if ('locale' in i18nInstance) {
    i18nInstance.locale = normalized;
  }
}
